import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import secureLocalStorage from 'react-secure-storage';

import { serverRequest } from '../../util/server_request';
import { extractErrorMessage } from '../extractErrorMessage';
import { notifyUser } from '../../util/util';

const notifiedErrors = new Set();
const notifyOnce = (type, message, title = '') => {
  if (!notifiedErrors.has(message)) {
    notifiedErrors.add(message);
    notifyUser(type, message, title);
  }
};

// Hook to fetch member recommendations
export function useMemberRecommendations() {
  return useQuery({
    queryKey: ['upsell', 'member-recommendations'],
    queryFn: async () => {
      try {
        const res = await serverRequest('GET', `upsell/member-recommendations`);

        // If API returns error property, surface it
        if (res?.error) {
          // Map known errors to friendly messages if needed
          const mapped = res.error === 'Not Found.' ? 'No recommendations found.' : res.error;
          notifyOnce('error', mapped, 'Upsell');
          throw new Error(mapped);
        }

        if (!res || !res.success) {
          const msg = res?.message || 'Failed to fetch recommendations';
          notifyOnce('error', msg, 'Upsell');
          throw new Error(msg);
        }

        return res;
      } catch (err) {
        const errorMessage = extractErrorMessage(err, 'Failed to fetch recommendations');
        notifyOnce('error', errorMessage, 'Upsell');
        throw new Error(errorMessage);
      }
    },
    enabled: !!secureLocalStorage.getItem('token'),
    staleTime: 5 * 60 * 1000,
  });
}

// Hook to apply a recommendation (POST)
export function useApplyRecommendation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (recommendation) => {
      try {
        const res = await serverRequest('POST', `upsell/apply-recommendation/${encodeURIComponent(recommendation)}`);

        if (res?.error) {
          notifyOnce('error', res.error, 'Upsell');
          throw new Error(res.error);
        }

        if (!res || !res.success) {
          const msg = res?.message || 'Failed to apply recommendation';
          notifyOnce('error', msg, 'Upsell');
          throw new Error(msg);
        }

        // Invalidate recommendations so UI can refetch if needed
        qc.invalidateQueries(['upsell', 'member-recommendations']);
        return res;
      } catch (err) {
        const errorMessage = extractErrorMessage(err, 'Failed to apply recommendation');
        notifyOnce('error', errorMessage, 'Upsell');
        throw new Error(errorMessage);
      }
    }
  });
}

// Hook to upgrade product (POST)
export function useUpgradeProduct() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (recommendationId) => {
      try {
        const formData = new FormData();
        formData.append('channel', 'App');

        const res = await serverRequest('POST', `upsell/upgrade-product/${encodeURIComponent(recommendationId)}`, formData);

        if (res?.error) {
          notifyOnce('error', res.error, 'Upsell');
          throw new Error(res.error);
        }

        if (!res || !res.success) {
          const msg = res?.message || 'Failed to upgrade product';
          notifyOnce('error', msg, 'Upsell');
          throw new Error(msg);
        }

        // Invalidate recommendations
        qc.invalidateQueries(['upsell', 'member-recommendations']);
        qc.invalidateQueries(['upsell', 'accepted-recommendations']);
        return res;
      } catch (err) {
        const errorMessage = extractErrorMessage(err, 'Failed to upgrade product');
        notifyOnce('error', errorMessage, 'Upsell');
        throw new Error(errorMessage);
      }
    }
  });
}

// Hook to dismiss recommendation (PUT)
export function useDismissRecommendation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (recommendationId) => {
      try {
        const res = await serverRequest('PUT', `upsell/dismiss-recommendation/${encodeURIComponent(recommendationId)}`, '');

        if (res?.error) {
          notifyOnce('error', res.error, 'Upsell');
          throw new Error(res.error);
        }

        if (!res || !res.success) {
          const msg = res?.message || 'Failed to dismiss recommendation';
          notifyOnce('error', msg, 'Upsell');
          throw new Error(msg);
        }

        // Invalidate recommendations
        qc.invalidateQueries(['upsell', 'member-recommendations']);
        return res;
      } catch (err) {
        const errorMessage = extractErrorMessage(err, 'Failed to dismiss recommendation');
        notifyOnce('error', errorMessage, 'Upsell');
        throw new Error(errorMessage);
      }
    }
  });
}

// Hook to fetch accepted recommendations
export function useAcceptedRecommendations() {
  return useQuery({
    queryKey: ['upsell', 'accepted-recommendations'],
    queryFn: async () => {
      try {
        const res = await serverRequest('GET', `upsell/accepted-recommendations`);

        if (res?.error) {
          const mapped = res.error === 'Not Found.' ? 'No accepted recommendations found.' : res.error;
          notifyOnce('error', mapped, 'Upsell');
          throw new Error(mapped);
        }

        if (!res || !res.success) {
          const msg = res?.message || 'Failed to fetch accepted recommendations';
          notifyOnce('error', msg, 'Upsell');
          throw new Error(msg);
        }

        return res;
      } catch (err) {
        const errorMessage = extractErrorMessage(err, 'Failed to fetch accepted recommendations');
        notifyOnce('error', errorMessage, 'Upsell');
        throw new Error(errorMessage);
      }
    },
    enabled: !!secureLocalStorage.getItem('token'),
    staleTime: 5 * 60 * 1000,
  });
}
